
import { useCallback, useEffect, useState } from 'react';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import { Button } from '@/components/ui/button';

interface PauseStatus {
  companyId: string;
  isPaused: boolean;
  pausedAt: string | null;
  pausedByToken: string | null;
  pauseReason: string | null;
}

const formatDate = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export default function CompanyPausePanel({ companyId }: { companyId: string }) {
  const [status, setStatus] = useState<PauseStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminHttpClient.get<PauseStatus>(getAdminEndpointUrl('pause', 'status', { companyId }));
      setStatus(data);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handlePause = async () => {
    if (!reason.trim()) {
      alert('El motivo es obligatorio');
      return;
    }
    setSaving(true);
    try {
      await adminHttpClient.put(getAdminEndpointUrl('pause', 'toggle', { companyId }), { isPaused: true, reason });
      setModalOpen(false);
      setReason('');
      await refresh();
    } catch (err) {
      alert(`Error: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleUnpause = async () => {
    if (!confirm('¿Despausar este comercio? Volverá a poder solicitar y recibir adelantos.')) return;
    setSaving(true);
    try {
      await adminHttpClient.put(getAdminEndpointUrl('pause', 'toggle', { companyId }), { isPaused: false });
      await refresh();
    } catch (err) {
      alert(`Error: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !status) return <div className="rounded-lg border p-6 text-sm text-muted-foreground">Cargando estado de pausa…</div>;
  if (!status) return null;

  return (
    <section className="rounded-xl border bg-card p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold">Estado del comercio</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Mientras el comercio esté pausado FLUX rechaza la creación y aprobación de adelantos.
          </p>
        </div>
        {status.isPaused ? (
          <Button variant="outline" size="sm" onClick={handleUnpause} disabled={saving}>
            <span className="material-symbols-outlined mr-1 text-base">play_arrow</span>
            Despausar
          </Button>
        ) : (
          <Button variant="destructive" size="sm" onClick={() => setModalOpen(true)}>
            <span className="material-symbols-outlined mr-1 text-base">pause</span>
            Pausar comercio
          </Button>
        )}
      </div>

      <div className="mt-4">
        {status.isPaused ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm dark:border-red-900 dark:bg-red-950/30">
            <div className="flex items-center gap-2 font-medium text-red-700 dark:text-red-400">
              <span className="material-symbols-outlined">pause_circle</span>
              <span>Comercio pausado</span>
            </div>
            <dl className="mt-3 grid grid-cols-1 gap-1 text-xs text-muted-foreground sm:grid-cols-2">
              <div>
                <dt className="inline font-medium">Desde:</dt> <dd className="inline">{status.pausedAt ? formatDate(status.pausedAt) : '—'}</dd>
              </div>
              <div>
                <dt className="inline font-medium">Token admin:</dt> <dd className="inline font-mono">{status.pausedByToken ?? '—'}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="inline font-medium">Motivo:</dt> <dd className="inline">{status.pauseReason ?? '—'}</dd>
              </div>
            </dl>
          </div>
        ) : (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-400">
            <span className="material-symbols-outlined mr-2 align-middle">check_circle</span>
            Comercio activo. Puede solicitar y recibir adelantos normalmente.
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-background p-6 shadow-xl">
            <h3 className="text-lg font-semibold">Pausar comercio</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              El comercio no podrá crear ni recibir adelantos nuevos hasta que lo despauses. Los adelantos ya desembolsados siguen su cobranza normalmente.
            </p>
            <label className="mt-4 block text-sm">
              Motivo (obligatorio)
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="mt-1 w-full rounded-md border px-3 py-2"
                rows={4}
                placeholder="Ej. cambio de cuenta HIGH no confirmado, sospecha de fraude, etc."
                required
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button type="button" variant="destructive" onClick={handlePause} disabled={saving}>
                {saving ? 'Pausando…' : 'Pausar comercio'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
